import React, { useContext, useState, useEffect } from 'react';
import {
  InputField,
  RadioGroupField,
  SelectField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import styles from './RPSOCRBeneficiaryInfo.css';
import { FlowContext } from '../../core/flow-engine';
import {
  formatSsnForDisplay,
  handleInputFormat
} from '../../flows/shared/utils';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import { beneRelationshipOptions, getBeneTaxIDLabel } from './util';
import { sum, titleCaseForString, withDefault } from '../../core/util';
import { rpsBeneficiaryCatogories } from '../../flows/front-office/shared/utils';
import { middlenameValidation } from '../../flows/shared/validations';

const RPSOCRRenderSecondaryEntitySection = ({
  showSecondaryBeneDistributionError,
  secondaryEntityType,
  idx,
  makeName,
  getDefault,
  secondaryBeneDistributionAction
}) => {
  const {
    formProps: { secondaryBeneSsn, secondaryBenePercentDistribution }
  } = useContext(FlowContext);

  const [ssnValue, setSSNValue] = useState('');

  return (
    <div className="qv-row">
      <div className="qv-col">
        <InputField
          title="% of Distribution"
          maxLength="3"
          {...secondaryBenePercentDistribution}
          name={makeName(secondaryBenePercentDistribution.name)}
          defaultValue={getDefault(secondaryBenePercentDistribution.name)}
          onChange={event =>
            secondaryBeneDistributionAction(idx, event.target.value)
          }
        />
        {showSecondaryBeneDistributionError && (
          <div className="bene-relationship-error">
            The total for Secondary Beneficiaries must equal 100%. Please
            re-enter.
          </div>
        )}
      </div>

      <div className="qv-col">
        <InputField
          title={getBeneTaxIDLabel(
            secondaryEntityType[idx],
            rpsBeneficiaryCatogories.INDIVIDUAL
          )}
          placeholder="000-00-0000"
          maxLength="11"
          onChange={evt => handleInputFormat(evt, 'SSN', setSSNValue)}
          {...secondaryBeneSsn}
          name={makeName(secondaryBeneSsn.name)}
          value={withDefault(
            ssnValue,
            formatSsnForDisplay(getDefault(secondaryBeneSsn.name))
          )}
        />
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

const RPSOCRRenderSecondaryBeneRelationsShipDOBInputs = ({
  showSecondaryBeneRelationshipError,
  idx,
  makeName,
  getDefault,
  secondaryBeneRelationshipAction
}) => {
  const {
    formProps: { secondaryBeneRelationship, secondaryBeneDob }
  } = useContext(FlowContext);

  const [dobValue, setDobValue] = useState('');

  return (
    <div className="qv-row">
      <div className="qv-col">
        <SelectField
          title="Relationship"
          {...secondaryBeneRelationship}
          name={makeName(secondaryBeneRelationship.name)}
          defaultValue={titleCaseForString(
            getDefault(secondaryBeneRelationship.name)
          )}
          onChange={event =>
            secondaryBeneRelationshipAction(idx, event.target.value)
          }>
          <option hidden value="">
            Select one
          </option>
          {beneRelationshipOptions.map(item => (
            <option key={item} value={item} data-name={item}>
              {item}
            </option>
          ))}
        </SelectField>
        {showSecondaryBeneRelationshipError && (
          <div className="bene-relationship-error">
            Spouse was selected more than once as a beneficiary relationship.
            Please select it once only.
          </div>
        )}
      </div>
      <div className="qv-col">
        <InputField
          type="text"
          title="Date of Birth"
          aria-describedby="hint"
          onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          {...secondaryBeneDob}
          name={makeName(secondaryBeneDob.name)}
          value={withDefault(dobValue, getDefault(secondaryBeneDob.name))}
        />
        <dd id="hint" style={{ display: 'none' }}>
          This is a required field. Please enter a two digit month, a two digit
          day and a four digit year for the Date of Birth value.
        </dd>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

const RPSOCRRenderSecondaryBeneBox = ({
  secondaryBeneficiaries = [],
  showSecondaryBeneRelationshipError,
  setShowSecondaryBeneRelationshipError,
  showSecondaryBeneDistributionError,
  setShowSecondaryBeneDistributionError
}) => {
  const {
    formProps: {
      secondaryBeneEntityName,
      secondaryBeneFirstName,
      secondaryBeneMiddleName,
      secondaryBeneLastName,
      secondaryBeneDob,
      secondaryBeneSsn,
      secondaryBeneRelationship,
      secondaryBenePercentDistribution,
      secondaryBeneCategoryType
    }
  } = useContext(FlowContext);

  const [secondaryBeneRelationships, setSecondaryBeneRelationships] = useState(
    secondaryBeneficiaries.map(person => person.relationship)
  );
  const [secondaryBeneDistribution, setSecondaryBeneDistributions] = useState(
    secondaryBeneficiaries.map(person => person.percentageDistribution)
  );

  const [secondaryEntityType, setSecondaryEntityType] = useState(
    secondaryBeneficiaries.map(person => person.beneCategoryType)
  );

  useEffect(() => {
    const totalSecondaryBeneDistributionPercent = [
      ...secondaryBeneDistribution
    ];
    const percentSum = sum(totalSecondaryBeneDistributionPercent);

    if (percentSum !== 100) {
      setShowSecondaryBeneDistributionError(true);
    } else {
      setShowSecondaryBeneDistributionError(false);
    }
    return () => {
      setShowSecondaryBeneDistributionError(false);
      setShowSecondaryBeneRelationshipError(false);
    };
  }, []);

  /* (1) updates bene relationship array when a section is removed and
     (2) filters SPOUSE relationships and sets the error state accordingly
  */
  const handleRemoveSecondaryBeneRelationshipSection = idx => {
    const filteredSecondaryBeneRelationshipNames = secondaryBeneRelationships.filter(
      (_, index) => index !== idx
    );
    const filteredSecondaryBeneRelationSpouse = filteredSecondaryBeneRelationshipNames.filter(
      rel => rel === 'Spouse'
    );
    if (filteredSecondaryBeneRelationSpouse.length < 2) {
      setShowSecondaryBeneRelationshipError(false);
    }
    setSecondaryBeneRelationships(filteredSecondaryBeneRelationshipNames);
  };

  const handleRemoveSecondaryBeneDistributionSection = idx => {
    const filteredSecondaryBeneDistributionNames = secondaryBeneDistribution.filter(
      (_, index) => index !== idx
    );
    setSecondaryBeneDistributions(filteredSecondaryBeneDistributionNames);

    const newPercentSum = sum(filteredSecondaryBeneDistributionNames);
    if (newPercentSum === 100) {
      setShowSecondaryBeneDistributionError(false);
    } else {
      setShowSecondaryBeneDistributionError(true);
    }
  };

  const defaults = {
    secondaryBenes: secondaryBeneficiaries.map(person => ({
      [secondaryBeneCategoryType.name]: person.beneCategoryType,
      [secondaryBeneFirstName.name]: person.name?.firstName,
      [secondaryBeneMiddleName.name]: person.name?.middleName,
      [secondaryBeneLastName.name]: person.name?.lastName,
      [secondaryBeneEntityName?.name]: person.entityName,
      [secondaryBenePercentDistribution.name]: person.percentageDistribution,
      [secondaryBeneDob.name]: person.dateOfBirth,
      [secondaryBeneSsn.name]: person.taxID,
      [secondaryBeneRelationship.name]: person.relationship
    }))
  };

  const secondaryBeneRelationshipAction = (idx, value) => {
    const updatedSecondaryBeneRelationships = [...secondaryBeneRelationships];
    updatedSecondaryBeneRelationships[idx] = value;

    const filteredSecondaryBeneRelationships = updatedSecondaryBeneRelationships.filter(
      rel => {
        return rel === 'Spouse';
      }
    );
    if (filteredSecondaryBeneRelationships.length >= 2) {
      setShowSecondaryBeneRelationshipError(true);
      setSecondaryBeneRelationships(updatedSecondaryBeneRelationships);
    } else {
      setShowSecondaryBeneRelationshipError(false);
      setSecondaryBeneRelationships(updatedSecondaryBeneRelationships);
    }
  };

  const secondaryBeneDistributionAction = (idx, value) => {
    const updatedSecondaryBeneDistributionships = [
      ...secondaryBeneDistribution
    ];
    updatedSecondaryBeneDistributionships[idx] = Number(value);

    const percentSum = sum(updatedSecondaryBeneDistributionships);

    if (percentSum !== 100) {
      setShowSecondaryBeneDistributionError(true);
      setSecondaryBeneDistributions(updatedSecondaryBeneDistributionships);
    } else {
      setShowSecondaryBeneDistributionError(false);
      setSecondaryBeneDistributions(updatedSecondaryBeneDistributionships);
    }
  };

  const secondaryEntityTypeAction = (idx, value) => {
    const updatedSecondaryEntityTypeAction = [...secondaryEntityType];
    updatedSecondaryEntityTypeAction[idx] = value;

    setSecondaryEntityType(updatedSecondaryEntityTypeAction);
  };

  const { sections, addSection } = useRepeatedSection(
    'secondaryBenes',
    defaults
  );

  return (
    <>
      {sections.map(({ key, getDefault, makeName, removeSection }, idx) => {
        return (
          <div key={key} className="section">
            <div className="remove-section-button">
              <MFBeaconButton
                id="removeSecondaryBeneBtn"
                data-name="removeSecondaryBeneBtn"
                noButtonMargin
                type="button"
                onClick={() => {
                  removeSection();
                  handleRemoveSecondaryBeneRelationshipSection(idx);
                  handleRemoveSecondaryBeneDistributionSection(idx);
                }}
                variant="tertiary"
                label="X"
              />
            </div>
            <div className="review-section">
              <div className="personal-info">
                <div className="qv-row">
                  <div className="bene-option-input">
                    <RadioGroupField
                      title="Beneficiary Type"
                      options={[
                        {
                          title: 'Individual',
                          value: rpsBeneficiaryCatogories.INDIVIDUAL,
                          defaultChecked:
                            secondaryEntityType[idx] ===
                            rpsBeneficiaryCatogories.INDIVIDUAL
                        },
                        {
                          title: 'Entity',
                          value: rpsBeneficiaryCatogories.ENTITY,
                          defaultChecked:
                            secondaryEntityType[idx] ===
                            rpsBeneficiaryCatogories.ENTITY
                        }
                      ]}
                      vertical
                      onChange={event =>
                        secondaryEntityTypeAction(idx, event.target.value)
                      }
                      {...secondaryBeneCategoryType}
                      name={makeName(secondaryBeneCategoryType.name)}
                    />
                  </div>
                </div>

                {secondaryEntityType[idx] ===
                  rpsBeneficiaryCatogories.INDIVIDUAL && (
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="First Name"
                        {...secondaryBeneFirstName}
                        name={makeName(secondaryBeneFirstName.name)}
                        defaultValue={getDefault(secondaryBeneFirstName.name)}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Middle Name"
                        {...secondaryBeneMiddleName}
                        name={makeName(secondaryBeneMiddleName.name)}
                        defaultValue={getDefault(secondaryBeneMiddleName.name)}
                        validation={middlenameValidation}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Last Name"
                        {...secondaryBeneLastName}
                        name={makeName(secondaryBeneLastName.name)}
                        defaultValue={getDefault(secondaryBeneLastName.name)}
                      />
                    </div>
                  </div>
                )}
                {secondaryEntityType[idx] ===
                  rpsBeneficiaryCatogories.ENTITY && (
                  <div className="qv-row">
                    <InputField
                      type="text"
                      title="Trust/Entity Name"
                      {...secondaryBeneEntityName}
                      name={makeName(secondaryBeneEntityName.name)}
                      defaultValue={getDefault(secondaryBeneEntityName.name)}
                      maxLength="70"
                    />
                  </div>
                )}

                {secondaryEntityType[idx] !== undefined && (
                  <RPSOCRRenderSecondaryEntitySection
                    showSecondaryBeneDistributionError={
                      showSecondaryBeneDistributionError
                    }
                    secondaryEntityType={secondaryEntityType}
                    idx={idx}
                    makeName={makeName}
                    getDefault={getDefault}
                    secondaryBeneDistributionAction={
                      secondaryBeneDistributionAction
                    }
                  />
                )}

                {secondaryEntityType[idx] !==
                  rpsBeneficiaryCatogories.INDIVIDUAL || (
                  <RPSOCRRenderSecondaryBeneRelationsShipDOBInputs
                    showSecondaryBeneRelationshipError={
                      showSecondaryBeneRelationshipError
                    }
                    idx={idx}
                    makeName={makeName}
                    getDefault={getDefault}
                    secondaryBeneRelationshipAction={
                      secondaryBeneRelationshipAction
                    }
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div>
        {sections.length < 4 && (
          <MFBeaconButton
            id="addSecondaryBeneBtn"
            data-name="addSecondaryBeneBtn"
            type="button"
            onClick={addSection}
            variant="tertiary"
            label="+ Add secondary beneficiary"
          />
        )}
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default RPSOCRRenderSecondaryBeneBox;
